import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Request, RequestType } from "../../models/Request";
import { Disposition } from "../../models/Disposition";
import { ProductType } from "../../models/Product";
import { getAllDispositionsByDate } from "../../services/NewVersionService";
import { Utility } from "../../core/Utility";
import "./DispositionsByRequestOverviewNew.scss";

export interface DispositionsByRequestOverviewProps {
  visible: boolean;
  handleClose: () => void;
  request: Request;
  dateFrom: Date;
  buttonInactiveStyle?: React.CSSProperties;
}

export const DispositionsByRequestOverviewNew = (
  p: DispositionsByRequestOverviewProps,
) => {
  const { visible, handleClose, request, dateFrom, buttonInactiveStyle } = p;

  const { t } = useTranslation();

  const [dispositionsByRequest, setDispositionsByRequest] = useState<
    Disposition[]
  >([]);
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    setProcessing(true);
    getAllDispositionsByDate(dateFrom)
      .then((response) => {
        const allNewDispositions = response.data.result;
        const filteredDisposionByRequest = allNewDispositions.filter(
          (item: any) => item?.request?.id == request?.id,
        );
        setDispositionsByRequest(filteredDisposionByRequest);
      })
      .finally(() => setProcessing(false));
  }, [visible]);

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) addButtonInactiveStyle = buttonInactiveStyle;

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 16 }}></i>
      </span>
      <span>{t(`Pregled dispozicija za izabrani zahtev`)}</span>
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Zatvori")}
        onButtonClick={handleClose}
        addStyle={addButtonInactiveStyle}
      />
    </div>
  );

  return (
    <>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: "65vw" }}
        className="dispositions-overview-modal"
        footer={footer}
        blockScroll={false}
        draggable={false}
        onHide={handleClose}
      >
        <div className="dispositions-overview-body">
          {processing ? (
            <ProgressSpinner />
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tr
                style={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #198754",
                }}
              >
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Datum")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Broj")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Roba")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Utovarena količina")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Mesto utovara")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Mesto istovara")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Vozilo")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Vozač")}
                </th>
              </tr>
              {dispositionsByRequest.map((disposition) => (
                <tr style={{ borderBottom: "1px solid #198754" }}>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {Utility.formatDate(new Date(disposition.dateFrom as Date))}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {disposition?.dispositionNumberFormatted ?? ''}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {disposition.request?.product?.type ===
                    ProductType.MercantileGoods
                      ? disposition.request.product.name
                      : JSON.parse(
                          disposition.repromaterialDispositionProductsJSON as string,
                        )?.map((item: any) => {
                          //@ts-ignore
                          return `${disposition?.repromaterialDispositionProducts?.find((oneDisp) => oneDisp?.id == item?.id)?.name}${"; "}`;
                        })}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {disposition.request?.product?.type ===
                    ProductType.MercantileGoods
                      ? `${disposition?.mercantileAmount} kg`
                      : JSON.parse(
                          disposition.repromaterialDispositionProductsJSON as string,
                        ).map((item: any) => {
                          return `${item.amount !== "" ? item.amount : 0}${"; "}`;
                        })}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {request?.place}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {request?.type === RequestType.Import ||
                    request?.type === RequestType.Relocation
                      ? disposition.inputLocation
                      : disposition?.outputLocation}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {disposition.registrationNumber?.toUpperCase()}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {disposition.driverName}
                  </td>
                </tr>
              ))}
            </table>
          )}
        </div>
      </Dialog>
    </>
  );
};
